import React, { Component } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { reduxForm, formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';

import {
  getManufacturers,
  getResinsByManufacturerId,
  getPrepregOptionsByResinId,
  getDefaultResinId,
} from '../../../reducers'
import { prepregsIdentical } from '../../../lib/material';
import {
  selectOptionPropType,
  materialWithCountPropType
} from '../../../lib/shapes';
import { Prepreg, Material } from '../../../models';
import { EDIT_PREPREG_FORM } from '../../../constants/globals';
import EditPrepregFields from './EditPrepregFields';
import EditFormActions from './EditFormActions';
import PrepregSummary from './PrepregSummary';
import Warnings from './LayerWarnings';
import {
  calcThickness,
  calcGlassThickness,
  calcPressedThickness,
  validate
} from '../../../lib/prepreg';

const formName = EDIT_PREPREG_FORM; 

export class EditPrepregForm extends Component {
  constructor(props) {
    super(props);
    this.initializeForm();
  }

  onSubmit(value) {
    const { onSubmit, handleSubmit } = this.props;
    handleSubmit(onSubmit)(value);
  }

  initializeForm() {
    const { initialize, layer } = this.props;
    const { manufacturerId, name, resinId } = layer;
    initialize({
      name,
      resin: {
        manufacturerId,
        id: resinId,
      }
    });
  }

  render() {
    const {
      defaultResinId,
      manufacturerOptions,
      resinOptions,
      materialOptions,
      nameValue,
      selectedMaterials,
      selectedResinId,
      layer,
      layers,
      mirror,
      onCancel,
      pristine,
      invalid,
      applyMirror,
      lengthUnit
    } = this.props;

    const selectedArray = selectedMaterials ?
      Object.keys(selectedMaterials).map(key => selectedMaterials[key]) :
      []; 
    const selectionChanged = !prepregsIdentical(selectedArray, layer.material)
    const nameChanged =  nameValue !== layer.name;
    // FIXME: Just clicking apply mirror checkbox will not enable submit button
    // As the form is still considered to be invalid. Investigate this issue.
    const canSubmit = Boolean(!(pristine || invalid) && (selectionChanged || nameChanged || applyMirror));
    const onSubmit = this.onSubmit.bind(this);
    const materials = materialOptions
      .sort((a, b) => a.thickness - b.thickness);

    const thickness = selectedArray.length > 0
      ? calcThickness(selectedArray)
      : layer.thickness;
    const glassThickness = selectedArray.length > 0
      ? calcGlassThickness(selectedArray)
      : layer.glassThickness;
    const resinId = selectedArray.length > 0
      ? selectedArray[0].material.resinId
      : layer.resinId ;
    const { index } = layer;
    const thisPrepreg = {
      ...layer,
      index,
      thickness,
      glassThickness,
      resinId
    }
    
    const pressedThickness = calcPressedThickness(thisPrepreg, index, layers);

    const { warnings } = validate(thisPrepreg, defaultResinId);

    return (
      <form onSubmit={onSubmit}>
        <EditPrepregFields
          layer={layer}
          manufacturerOptions={manufacturerOptions}
          resinOptions={resinOptions}
          materialOptions={materials}
          selectedResinId={selectedResinId}
          selectedMaterials={selectedMaterials}
          lengthUnit={lengthUnit}
        />
        <PrepregSummary
          {...{
            thickness,
            pressedThickness,
            glassThickness,
            lengthUnit
          }}
        />
        <Warnings texts={warnings} />
        <EditFormActions
          layer={layer}
          mirror={mirror}
          onCancel={onCancel}
          canSubmit={canSubmit}
        />
      </form>
    );
  }
}

const selector = formValueSelector(formName);
function mapStateToProps(state){
  const resin = selector(state, 'resin');
  const selectedManufacturerId = resin ? resin.manufacturerId : undefined;
  const selectedResinId = resin ? resin.id : undefined;
  const selectedMaterials = selector(state, 'materials');
  const nameValue = selector(state, 'name');
  const applyMirror = selector(state, 'applyMirror');

  return {
    defaultResinId: getDefaultResinId(state),
    manufacturerOptions: getManufacturers(state),
    resinOptions: getResinsByManufacturerId(state, selectedManufacturerId),
    materialOptions: getPrepregOptionsByResinId(state, selectedResinId),
    selectedResinId,
    selectedMaterials,
    nameValue,
    applyMirror,
  };
}

const formConfig = {
  form: formName,
};

EditPrepregForm.propTypes = {
  // props from the parent component
  layer:    PropTypes.instanceOf(Prepreg).isRequired,
  layers:   PropTypes.arrayOf(PropTypes.instanceOf(Material)).isRequired,
  mirror:   PropTypes.instanceOf(Prepreg).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  lengthUnit: PropTypes.string.isRequired,

  // redux-form props
  initialize: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,

  // mapStateToProps
  nameValue: PropTypes.string,
  applyMirror: PropTypes.bool,
  defaultResinId: PropTypes.string.isRequired,
  manufacturerOptions: PropTypes
    .arrayOf(PropTypes.shape(selectOptionPropType))
    .isRequired,
  resinOptions: PropTypes
    .arrayOf(PropTypes.shape(selectOptionPropType))
    .isRequired,
  materialOptions: PropTypes
    .arrayOf(PropTypes.shape(materialWithCountPropType))
    .isRequired,
  selectedResinId: PropTypes.string,
  selectedMaterials: PropTypes.objectOf(
    PropTypes.shape(materialWithCountPropType)
  )
};

EditPrepregForm.defaultProps = {
  applyMirror: false,
  selectedMaterials: {},
  selectedResinId: undefined,
  nameValue: undefined
}

export default compose(
  reduxForm(formConfig),
  connect(mapStateToProps )
) (EditPrepregForm);



// WEBPACK FOOTER //
// ./src/components/StackupDesign/EditLayer/EditPrepregForm.js